import {
  AccountStatuses,
  BreadTransaction,
  FirestoreCollectionNames,
  FirestoreDocCategory,
  FirestoreDocBudget,
  FirestoreDocRule,
  FirestoreDocTag,
  FirestoreDocUser,
  firestoreDocBudgetSchema,
  firestoreDocCategorySchema,
  firestoreDocRuleSchema,
  firestoreDocTagSchema,
  firestoreDocTransactionSchema,
  categoryUpdateFromRules,
  parseBreadTransaction,
  firestoreDocCategoryGroupSchema,
  PlaidItemSafe,
  PlaidItemSafeSchema,
} from "breadcommon";
import { FirestoreDocTransaction } from "breadcommon";
import { FirestoreDocAccount } from "breadcommon";
import {
  BreadAccount,
  BreadInstitution,
  firestoreDocAccountSchema,
  firestoreDocInstitutionSchema,
  parseBreadAccount,
  parseBreadInstitution,
} from "breadcommon";
import { getApp } from "firebase/app";
import {
  getFirestore,
  onSnapshot,
  collection,
  query,
  deleteDoc,
  doc,
  getDocs,
  writeBatch,
  setDoc,
  getDoc,
  Unsubscribe,
  WriteBatch,
  updateDoc,
  deleteField,
  DocumentReference,
  DocumentData,
  FieldValue,
} from "firebase/firestore";
import dayjs, { Dayjs } from "dayjs";
import { FirestoreDocCategoryGroup } from "breadcommon";
import { ItemsGroup } from "../rye/RyeDraggableGroupedList";

type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

export async function firestoreCreateUser(userId: string) {
  const user: FirestoreDocUser = {
    uid: userId,
  };

  const db = getFirestore(getApp());
  return setDoc(doc(db, FirestoreCollectionNames.users.name, userId), user);
}

export function firestoreInstitutionsSubscribe(
  userId: string,
  save: (map: Map<string, BreadInstitution>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.institutions.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let institutions = new Map<string, BreadInstitution>();
      querySnapshot.forEach((institutionDoc) => {
        const institutionParseResult = firestoreDocInstitutionSchema.safeParse(
          institutionDoc.data(),
        );
        if (!institutionParseResult.success) {
          return;
        }
        const institution = parseBreadInstitution(institutionParseResult.data);
        if (!institution) {
          return;
        }
        institutions.set(institution.id, institution);
      });
      save(institutions);
    },
  );
}

export function firestorePlaidItemsSubscribe(
  userId: string,
  save: (map: Map<string, PlaidItemSafe>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.plaid_items.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let plaidItems = new Map<string, PlaidItemSafe>();
      querySnapshot.forEach((doc) => {
        // default behavior for parse/safeParse is to drop extra fields
        const parseResult = PlaidItemSafeSchema.safeParse(doc.data());
        if (parseResult.success) {
          plaidItems.set(parseResult.data.plaid_item_id, parseResult.data);
        }
      });
      save(plaidItems);
    },
  );
}

export function firestoreAccountsSubscribe(
  userId: string,
  save: (map: Map<string, BreadAccount>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.accounts.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let accounts = new Map<string, BreadAccount>();
      querySnapshot.forEach((accountDoc) => {
        const firestoreAccountParseResult = firestoreDocAccountSchema.safeParse(
          accountDoc.data(),
        );
        if (!firestoreAccountParseResult.success) {
          console.error(
            "Zod parse failed for account document:",
            accountDoc.id,
            firestoreAccountParseResult.error.format()
          );
          return;
        }
        const breadAccount = parseBreadAccount(
          firestoreAccountParseResult.data,
        );
        if (breadAccount === null) {
          return;
        }
        accounts.set(breadAccount.id, breadAccount);
      });
      save(accounts);
    },
  );
}

export async function firestoreUpdateAccount(
  userId: string,
  accountId: string,
  update: RecursivePartial<FirestoreDocAccount>,
  writeBatch: WriteBatch | null = null,
) {
  const db = getFirestore(getApp());

  if (writeBatch) {
    return writeBatch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.accounts.name,
        accountId,
      ),
      update,
      { merge: true },
    );
  }

  return setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.accounts.name,
      accountId,
    ),
    update,
    {
      merge: true,
    },
  );
}

async function firestoreDeleteTransactionsByAccountId(
  userId: string,
  accountId: string,
  batch: WriteBatch,
) {
  const db = getFirestore(getApp());
  const transactionsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
      ),
    ),
  );
  for (const t of transactionsSnapshot.docs) {
    const transaction = firestoreDocTransactionSchema.safeParse(t.data());
    if (
      transaction.success &&
      (transaction.data.plaid_data?.account_id === accountId ||
        transaction.data.plaid_investment_data?.account_id === accountId)
    ) {
      batch.delete(t.ref);
    }
  }
}

export async function firestoreDeleteAccountById(
  userId: string,
  accountToDelete: BreadAccount,
  // This is null when we are sort of canceling adding an account,
  // and we don't need to update the order positions of other accounts.
  accounts: BreadAccount[] | null,
  cascadeTransactionDeletions: boolean = false,
) {
  const db = getFirestore(getApp());

  if (accounts === null) {
    return deleteDoc(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.accounts.name,
        accountToDelete.id,
      ),
    );
  }

  // A batched write is just a transaction that only makes writes
  // https://firebase.google.com/docs/firestore/manage-data/transactions#batched-writes
  const batch = writeBatch(db);

  batch.delete(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.accounts.name,
      accountToDelete.id,
    ),
  );

  for (const account of accounts) {
    if (
      account.status !== AccountStatuses.NEW_UNSELECTED &&
      account.order_position > accountToDelete.order_position
    ) {
      firestoreUpdateAccount(
        userId,
        account.id,
        {
          manual_data: {
            order_position: account.order_position - 1,
          },
        },
        batch,
      );
    }
  }

  if (cascadeTransactionDeletions) {
    // need to await here because we are doing a read that is not part of a
    // firestore transaction inside this call
    await firestoreDeleteTransactionsByAccountId(
      userId,
      accountToDelete.id,
      batch,
    );
  }

  return batch.commit();
}

export function firestoreUpdateAccountsOrder(
  userId: string,
  newAccountIdsInOrder: string[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  let order_pos = 0;
  for (const accountId of newAccountIdsInOrder) {
    firestoreUpdateAccount(
      userId,
      accountId,
      { manual_data: { order_position: order_pos } },
      batch,
    );
    order_pos++;
  }

  return batch.commit();
}

export function firestoreTransactionsSubscribe(
  userId: string,
  save: (
    txnChanges: Map<string, BreadTransaction>,
    txnDeletions: string[],
  ) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
      ),
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let txnChanges = new Map<string, BreadTransaction>();
      let txnDeletions: string[] = [];
      querySnapshot.docChanges().forEach((docChange) => {
        if (docChange.type === "added" || docChange.type === "modified") {
          const txnParseResult = firestoreDocTransactionSchema.safeParse(
            docChange.doc.data(),
          );
          if (!txnParseResult.success) {
            return;
          }
          const txn = parseBreadTransaction(txnParseResult.data);
          if (!txn) {
            return;
          }
          txnChanges.set(txn.id, txn);
        } else if (docChange.type === "removed") {
          txnDeletions.push(docChange.doc.id);
        }
      });
      save(txnChanges, txnDeletions);
    },
  );
}

export async function firestoreUpdateTransaction(
  userId: string,
  transactionId: string,
  update: RecursivePartial<FirestoreDocTransaction>,
  writeBatch: WriteBatch | null = null,
) {
  const db = getFirestore(getApp());
  if (writeBatch) {
    return writeBatch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
        transactionId,
      ),
      update,
      { merge: true },
    );
  }
  return setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.transactions.name,
      transactionId,
    ),
    update,
    { merge: true },
  );
}

export function firestoreCategoryGroupsSubscribe(
  userId: string,
  save: (map: Map<string, FirestoreDocCategoryGroup>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.category_groups.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let categoryGroups = new Map<string, FirestoreDocCategoryGroup>();
      querySnapshot.docs.forEach((categoryGroupDoc) => {
        const categoryGroupParseResult =
          firestoreDocCategoryGroupSchema.safeParse(categoryGroupDoc.data());
        if (categoryGroupParseResult.success) {
          categoryGroups.set(
            categoryGroupParseResult.data.id,
            categoryGroupParseResult.data,
          );
        }
      });
      save(categoryGroups);
    },
  );
}

export async function firestoreCreateCategoryGroup(
  userId: string,
  categoryGroupWithoutId: Omit<FirestoreDocCategoryGroup, "id">,
): Promise<string> {
  const db = getFirestore(getApp());
  const newCategoryGroupDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.category_groups.name,
    ),
  );
  const newCategoryGroupId = newCategoryGroupDocRef.id;
  const categoryGroup: FirestoreDocCategoryGroup = {
    ...categoryGroupWithoutId,
    id: newCategoryGroupId,
  };

  await setDoc(newCategoryGroupDocRef, categoryGroup);
  return newCategoryGroupId;
}

export async function firestoreUpdateCategoryGroup(
  userId: string,
  categoryGroupId: string,
  update: RecursivePartial<FirestoreDocCategoryGroup>,
  writeBatch: WriteBatch | null = null,
) {
  const db = getFirestore(getApp());
  if (writeBatch) {
    return writeBatch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.category_groups.name,
        categoryGroupId,
      ),
      update,
      { merge: true },
    );
  }
  return setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.category_groups.name,
      categoryGroupId,
    ),
    update,
    {
      merge: true,
    },
  );
}

export function firestoreUpdateCategoryGroupsOrder(
  userId: string,
  newCategoryGroupIdsOrder: string[],
  incomingBatch: WriteBatch | null = null,
): Promise<void> | void {
  const db = getFirestore(getApp());
  let batch = incomingBatch;
  if (batch === null) {
    batch = writeBatch(db);
  }

  let i = 0;
  for (const id of newCategoryGroupIdsOrder) {
    firestoreUpdateCategoryGroup(
      userId,
      id,
      {
        order_position: i,
      },
      batch,
    );
    i++;
  }
  if (incomingBatch === null) {
    return batch.commit();
  }
}

export function firestoreDeleteCategoryGroupAndUpdateOrder(
  userId: string,
  categoryGroupToDelete: FirestoreDocCategoryGroup,
  categoryGroups: FirestoreDocCategoryGroup[],
  categories: FirestoreDocCategory[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  for (const category of categories) {
    if (category.category_group_id === categoryGroupToDelete.id)
      throw new Error(
        "Attempted to delete a category group that still has categories in it.",
      );
  }

  batch.delete(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.category_groups.name,
      categoryGroupToDelete.id,
    ),
  );
  firestoreUpdateCategoryGroupsOrder(
    userId,
    categoryGroups
      .filter((group) => group.id !== categoryGroupToDelete.id)
      .sort((a, b) => a.order_position - b.order_position)
      .map((group) => group.id),
    batch,
  );
  return batch.commit();
}

export function firestoreCategoriesSubscribe(
  userId: string,
  save: (map: Map<string, FirestoreDocCategory>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.categories.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let categories = new Map<string, FirestoreDocCategory>();
      querySnapshot.docs.forEach((categoryDoc) => {
        const categoryParseResult = firestoreDocCategorySchema.safeParse(
          categoryDoc.data(),
        );
        if (categoryParseResult.success) {
          categories.set(categoryParseResult.data.id, categoryParseResult.data);
        }
      });
      save(categories);
    },
  );
}

export async function firestoreCreateCategory(
  userId: string,
  categoryWithoutId: Omit<FirestoreDocCategory, "id">,
) {
  const db = getFirestore(getApp());
  const newCategoryDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.categories.name,
    ),
  );
  const newCategoryId = newCategoryDocRef.id;
  const category: FirestoreDocCategory = {
    ...categoryWithoutId,
    id: newCategoryId,
  };

  // add new category to all existing budgets
  const budgetsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.budgets.name,
      ),
    ),
  );
  budgetsSnapshot.docs.forEach(
    async (budgetDoc) =>
      await firestoreAddEmptyBudgetLineItem(
        userId,
        budgetDoc.id,
        newCategoryId,
      ),
  );
  return setDoc(newCategoryDocRef, category);
}

export async function firestoreUpdateCategory(
  userId: string,
  categoryId: string,
  update: RecursivePartial<FirestoreDocCategory>,
  writeBatch: WriteBatch | null = null,
) {
  const db = getFirestore(getApp());
  if (writeBatch) {
    return writeBatch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.categories.name,
        categoryId,
      ),
      update,
      { merge: true },
    );
  }
  return setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.categories.name,
      categoryId,
    ),
    update,
    {
      merge: true,
    },
  );
}

export function firestoreUpdateCategoriesOrder(
  userId: string,
  newGroupedCategories: ItemsGroup[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  for (const categoryGroup of newGroupedCategories) {
    let categoryIndex = 0;
    for (const category of categoryGroup.items) {
      firestoreUpdateCategory(
        userId,
        category.id,
        { category_group_id: categoryGroup.id, order_position: categoryIndex },
        batch,
      );
      categoryIndex++;
    }
  }
  return batch.commit();
}

export async function firestoreDeleteCategoryByIdAndUpdateRelationships(
  userId: string,
  categoryToDelete: FirestoreDocCategory,
  replacementCategoryId: string | null,
  categories: FirestoreDocCategory[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  batch.delete(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.categories.name,
      categoryToDelete.id,
    ),
  );

  // update other category's order positions
  for (const category of categories) {
    if (category.order_position > categoryToDelete.order_position) {
      firestoreUpdateCategory(
        userId,
        category.id,
        {
          order_position: category.order_position - 1,
        },
        batch,
      );
    }
  }

  // update/remove category from any transactions that had the deleted category
  const transactionsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
      ),
    ),
  );
  for (const t of transactionsSnapshot.docs) {
    const transaction = firestoreDocTransactionSchema.safeParse(t.data());
    if (
      transaction.success &&
      transaction.data.manual_data.category_id === categoryToDelete.id &&
      transaction.data.plaid_data?.transaction_id
    ) {
      firestoreUpdateTransaction(
        userId,
        transaction.data.plaid_data?.transaction_id,
        { manual_data: { category_id: replacementCategoryId } },
        batch,
      );
    }
  }

  // remove category from all existing budgets
  const budgetsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.budgets.name,
      ),
    ),
  );
  budgetsSnapshot.docs.forEach(async (budgetDoc) =>
    firestoreRemoveBudgetLineItem(
      userId,
      budgetDoc.id,
      categoryToDelete.id,
      batch,
    ),
  );
  return batch.commit();
}

export function firestoreRulesSubscribe(
  userId: string,
  save: (
    ruleChanges: Map<string, FirestoreDocRule>,
    ruleDeletions: string[],
  ) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.rules.name,
      ),
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let ruleChanges = new Map<string, FirestoreDocRule>();
      let ruleDeletions: string[] = [];
      querySnapshot.docChanges().forEach((docChange) => {
        if (docChange.type === "added" || docChange.type === "modified") {
          const ruleParseResult = firestoreDocRuleSchema.safeParse(
            docChange.doc.data(),
          );
          if (!ruleParseResult.success) {
            return;
          }
          ruleChanges.set(ruleParseResult.data.id, ruleParseResult.data);
        } else if (docChange.type === "removed") {
          ruleDeletions.push(docChange.doc.id);
        }
      });
      save(ruleChanges, ruleDeletions);
    },
  );
}

async function firestoreGetRules(
  userId: string,
): Promise<Map<string, FirestoreDocRule>> {
  const db = getFirestore(getApp());
  const rulesSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.rules.name,
      ),
    ),
  );
  let rules: Map<string, FirestoreDocRule> = new Map();
  for (const r of rulesSnapshot.docs) {
    const rule = firestoreDocRuleSchema.safeParse(r.data());
    if (rule.success) {
      rules.set(rule.data.id, rule.data);
    }
  }
  return rules;
}

/**
 * Re-applies rules to every unreviewed transaction. After a rule change, pass
 * the rules from just before it, so categories the old rules set still follow
 * the rules while categories the user (or an agent) picked are kept; see
 * categoryUpdateFromRules.
 */
export async function firestoreUpdateTransactionsAgainstRules(
  userId: string,
  rulesBeforeChange?: Map<string, FirestoreDocRule>,
): Promise<void> {
  const db = getFirestore(getApp());

  const batch = writeBatch(db);

  const rules = await firestoreGetRules(userId);

  const transactionsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
      ),
    ),
  );
  for (const t of transactionsSnapshot.docs) {
    const transactionParse = firestoreDocTransactionSchema.safeParse(t.data());
    if (transactionParse.success) {
      const breadTransaction: BreadTransaction | null = parseBreadTransaction(
        transactionParse.data,
      );
      if (!breadTransaction) {
        console.log("Null transaction while trying to apply Rules.");
        continue; // shouldn't happen, maybe shouldn't fail silently
      }
      if (breadTransaction.reviewed) {
        continue; // don't apply to Rules to transactions that have already been reviewed.
      }
      const update = categoryUpdateFromRules(
        breadTransaction,
        rules,
        rulesBeforeChange,
      );
      if (update === null) {
        continue; // nothing to change, or a category the user or an agent chose
      }
      firestoreUpdateTransaction(
        userId,
        breadTransaction.id,
        { manual_data: update },
        batch,
      );
    }
  }
  return batch.commit();
}

export async function firestoreAddRuleAndUpdateTransactions(
  userId: string,
  newRuleWithoutId: Omit<FirestoreDocRule, "id">,
): Promise<void> {
  const db = getFirestore(getApp());

  const rulesBeforeChange = await firestoreGetRules(userId);

  const newRuleDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.rules.name,
    ),
  );

  const newRule: FirestoreDocRule = {
    id: newRuleDocRef.id,
    ...newRuleWithoutId,
  };

  await setDoc(newRuleDocRef, newRule);

  return firestoreUpdateTransactionsAgainstRules(userId, rulesBeforeChange);
}

export async function firestoreDeleteRuleAndUpdateTransactions(
  userId: string,
  ruleIdToRemove: string,
) {
  const db = getFirestore(getApp());

  const rulesBeforeChange = await firestoreGetRules(userId);

  await deleteDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.rules.name,
      ruleIdToRemove,
    ),
  );

  return firestoreUpdateTransactionsAgainstRules(userId, rulesBeforeChange);
}

export async function firestoreUpdateRuleAndUpdateTransactions(
  userId: string,
  ruleID: string,
  updates: RecursivePartial<FirestoreDocRule>,
): Promise<void> {
  const db = getFirestore(getApp());

  const rulesBeforeChange = await firestoreGetRules(userId);

  await setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.rules.name,
      ruleID,
    ),
    updates,
    { merge: true },
  );

  return firestoreUpdateTransactionsAgainstRules(userId, rulesBeforeChange);
}

export async function firestoreUpdateRulesOrderAndUpdateTransactions(
  userId: string,
  newRuleIdsInOrder: string[],
): Promise<void> {
  const db = getFirestore(getApp());

  const rulesBeforeChange = await firestoreGetRules(userId);

  const batch = writeBatch(db);

  let orderPos = 0;

  for (const ruleId of newRuleIdsInOrder) {
    const updates: RecursivePartial<FirestoreDocRule> = {
      order_position: orderPos,
    };

    batch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.rules.name,
        ruleId,
      ),
      updates,
      { merge: true },
    );

    orderPos++;
  }

  await batch.commit();

  return firestoreUpdateTransactionsAgainstRules(userId, rulesBeforeChange);
}

export function firestoreBudgetsSubscribe(
  userId: string,
  save: (
    budgetChanges: Map<string, FirestoreDocBudget>,
    budgetDeletions: string[],
  ) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.budgets.name,
      ),
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      let budgetChanges = new Map<string, FirestoreDocBudget>();
      let budgetDeletions: string[] = [];
      querySnapshot.docChanges().forEach((docChange) => {
        if (docChange.type === "added" || docChange.type === "modified") {
          const budgetParseResult = firestoreDocBudgetSchema.safeParse(
            docChange.doc.data(),
          );
          if (!budgetParseResult.success) {
            return;
          }
          budgetChanges.set(docChange.doc.id, budgetParseResult.data);
        } else if (docChange.type === "removed") {
          budgetDeletions.push(docChange.doc.id);
        }
      });
      save(budgetChanges, budgetDeletions);
    },
  );
}

export async function firestoreCreateNewEmptyBudget(
  userId: string,
  name: string, // name of the budget
  beginDateTimestampSecs: Dayjs,
  endDateTimestampSecs: Dayjs,
  category_ids: string[],
  currency: string,
): Promise<string> {
  const db = getFirestore(getApp());

  const newBudgetDoc: FirestoreDocBudget = {
    name: name,
    begin_date_timestamp_secs: beginDateTimestampSecs.unix(),
    end_date_timestamp_secs: endDateTimestampSecs.unix(),
    currency: currency,
    line_items: Object.fromEntries(
      category_ids.map((category_id) => {
        return [
          category_id,
          {
            formula: "",
          },
        ];
      }),
    ),
  };

  const newBudgetDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
    ),
  );

  await setDoc(newBudgetDocRef, newBudgetDoc);
  return newBudgetDocRef.id;
}

export async function firestoreDuplicateBudget(
  userId: string,
  sourceBudgetId: string,
  newName: string,
): Promise<string> {
  const db = getFirestore(getApp());

  // Get the source budget document
  const sourceBudgetDocRef = doc(
    db,
    FirestoreCollectionNames.users.name,
    userId,
    FirestoreCollectionNames.users.budgets.name,
    sourceBudgetId,
  );

  const sourceBudgetSnapshot = await getDoc(sourceBudgetDocRef);
  if (!sourceBudgetSnapshot.exists()) {
    throw new Error("Source budget not found");
  }

  const sourceBudgetData = sourceBudgetSnapshot.data() as FirestoreDocBudget;

  const newBudgetDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
    ),
  );

  const newBudgetDoc: FirestoreDocBudget = {
    ...sourceBudgetData,
    name: newName,
  };

  await setDoc(newBudgetDocRef, newBudgetDoc);
  return newBudgetDocRef.id;
}

export async function firestoreOverwriteBudget(
  userId: string,
  budgetId: string,
  budgetDoc: FirestoreDocBudget,
) {
  const db = getFirestore(getApp());
  await setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
      budgetId,
    ),
    budgetDoc,
  );
}

export async function firestoreUpdateBudget(
  userId: string,
  budgetId: string,
  update: RecursivePartial<Omit<FirestoreDocBudget, "line_items">>,
) {
  const db = getFirestore(getApp());
  setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
      budgetId,
    ),
    update,
    { merge: true },
  );
}

export async function firestoreAddEmptyBudgetLineItem(
  userId: string,
  budgetId: string,
  categoryId: string,
) {
  await firestoreUpdateBudgetLineItem(userId, budgetId, categoryId, "");
}

export async function firestoreUpdateBudgetLineItem(
  userId: string,
  budgetId: string,
  categoryId: string,
  formula: string,
) {
  const db = getFirestore(getApp());
  const update: Pick<FirestoreDocBudget, "line_items"> = {
    line_items: { [categoryId]: { formula: formula } },
  };
  setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
      budgetId,
    ),
    update,
    { merge: true },
  );
}

export async function firestoreRemoveBudgetLineItem(
  userId: string,
  budgetId: string,
  categoryId: string,
  writeBatch: WriteBatch | null = null,
) {
  const db = getFirestore(getApp());
  const updateArgs: [
    DocumentReference<DocumentData, DocumentData>,
    { [x: string]: FieldValue },
  ] = [
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
      budgetId,
    ),
    {
      ["line_items." + categoryId]: deleteField(),
    },
  ];
  if (writeBatch !== null) {
    writeBatch.update(...updateArgs);
  } else {
    updateDoc(...updateArgs);
  }
}

export async function firestoreDeleteBudget(userId: string, budgetId: string) {
  const db = getFirestore(getApp());
  deleteDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.budgets.name,
      budgetId,
    ),
  );
}

export function firestoreTagsSubscribe(
  userId: string,
  save: (map: Map<string, FirestoreDocTag>) => void,
  markInitialLoadComplete: () => void,
): Unsubscribe {
  const db = getFirestore(getApp());
  return onSnapshot(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.tags.name,
    ),
    (querySnapshot) => {
      markInitialLoadComplete();
      const tags = new Map<string, FirestoreDocTag>();
      querySnapshot.docs.forEach((tagDoc) => {
        const parseResult = firestoreDocTagSchema.safeParse(tagDoc.data());
        if (parseResult.success) {
          tags.set(parseResult.data.id, parseResult.data);
        }
      });
      save(tags);
    },
  );
}

export async function firestoreCreateTag(
  userId: string,
  tagWithoutId: Omit<FirestoreDocTag, "id">,
): Promise<string> {
  const db = getFirestore(getApp());
  const newTagDocRef = doc(
    collection(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.tags.name,
    ),
  );
  const newTag: FirestoreDocTag = {
    ...tagWithoutId,
    id: newTagDocRef.id,
  };
  await setDoc(newTagDocRef, newTag);
  return newTagDocRef.id;
}

export async function firestoreUpdateTag(
  userId: string,
  tagId: string,
  update: RecursivePartial<FirestoreDocTag>,
): Promise<void> {
  const db = getFirestore(getApp());
  return setDoc(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.tags.name,
      tagId,
    ),
    update,
    { merge: true },
  );
}

export async function firestoreUpdateTagsOrder(
  userId: string,
  newTagIdsInOrder: string[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);
  newTagIdsInOrder.forEach((tagId, index) => {
    batch.set(
      doc(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.tags.name,
        tagId,
      ),
      { order_position: index },
      { merge: true },
    );
  });
  return batch.commit();
}

export async function firestoreDeleteTagAndRemoveFromTransactions(
  userId: string,
  tagId: string,
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  batch.delete(
    doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.tags.name,
      tagId,
    ),
  );

  const transactionsSnapshot = await getDocs(
    query(
      collection(
        db,
        FirestoreCollectionNames.users.name,
        userId,
        FirestoreCollectionNames.users.transactions.name,
      ),
    ),
  );
  for (const t of transactionsSnapshot.docs) {
    const parseResult = firestoreDocTransactionSchema.safeParse(t.data());
    if (!parseResult.success) continue;
    const currentTagIds = parseResult.data.manual_data.tag_ids ?? [];
    if (!currentTagIds.includes(tagId)) continue;
    batch.set(
      t.ref,
      {
        manual_data: {
          tag_ids: currentTagIds.filter((id) => id !== tagId),
        },
      },
      { merge: true },
    );
  }

  return batch.commit();
}

/**
 * Dev-only: Simulates a NO_ACCOUNTS error on an item by writing the error 
 * to the item and marking the provided accounts as disappeared.
 */
export async function firestoreDevForceNoAccounts(
  userId: string,
  itemId: string,
  accountIds: string[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  const noAccountsError = {
    error_type: "ITEM_ERROR",
    error_code: "NO_ACCOUNTS",
    error_message: "Forced NO_ACCOUNTS error for testing.",
    display_message: null,
  };

  // 1. Update the plaid item doc's sync_errors
  const itemRef = doc(
    db,
    FirestoreCollectionNames.users.name,
    userId,
    FirestoreCollectionNames.users.plaid_items.name,
    itemId,
  );
  batch.set(
    itemRef,
    {
      sync_errors: {
        account_sync_error: noAccountsError,
        transaction_sync_error: noAccountsError,
      },
    },
    { merge: true },
  );

  // 2. Set disappeared_from_plaid = true on all associated accounts
  for (const accountId of accountIds) {
    const accountRef = doc(
      db,
      FirestoreCollectionNames.users.name,
      userId,
      FirestoreCollectionNames.users.accounts.name,
      accountId,
    );
    batch.set(
      accountRef,
      {
        system_data: {
          disappeared_from_plaid: true,
        },
      },
      { merge: true },
    );
  }

  return batch.commit();
}

/**
 * Dev-only: Simulates a single account disappearing from Plaid.
 */
export async function firestoreDevSimulateAccountDisappeared(
  userId: string,
  accountId: string,
): Promise<void> {
  await firestoreUpdateAccount(userId, accountId, {
    system_data: {
      disappeared_from_plaid: true,
    },
  });
}

/**
 * Closes an account and compacts order positions of other active accounts.
 */
export async function firestoreCloseAccount(
  userId: string,
  accountId: string,
  activeAccounts: BreadAccount[],
): Promise<void> {
  const db = getFirestore(getApp());
  const batch = writeBatch(db);

  // 1. Close the target account
  firestoreUpdateAccount(
    userId,
    accountId,
    {
      system_data: {
        status: AccountStatuses.CLOSED,
        closed_timestamp_secs: dayjs().unix(),
      },
    },
    batch,
  );

  // 2. Compact order positions of the remaining active accounts
  const otherActiveAccountsInOrder = activeAccounts
    .filter((acc) => acc.id !== accountId)
    .sort((a, b) => a.order_position - b.order_position);

  let newOrderPos = 0;
  for (const acc of otherActiveAccountsInOrder) {
    firestoreUpdateAccount(
      userId,
      acc.id,
      {
        manual_data: {
          order_position: newOrderPos,
        },
      },
      batch,
    );
    newOrderPos++;
  }

  return batch.commit();
}

/**
 * Reopens an account and appends it to the end of the active list.
 */
export async function firestoreReopenAccount(
  userId: string,
  accountId: string,
  activeAccountsCount: number,
): Promise<void> {
  await firestoreUpdateAccount(userId, accountId, {
    system_data: {
      status: AccountStatuses.ACTIVE,
      closed_timestamp_secs: null,
    },
    manual_data: {
      order_position: activeAccountsCount,
    },
  });
}


